import { trans } from "@mongez/localization";
import type { Address } from "core/addresses";
import {
  getCitiesService,
  getRegionsService,
  getZipCodesService,
  parseError,
} from "core/shared";
import { endpoint } from "framework/config/endpoint";
import { useCallback, useEffect, useState } from "react";
import { errorToast } from "~/design-system/components";
import { getLocalizedValue } from "~/shared/urils/getLocalizedValue";

type Region = {
  id: number;
  name: string;
};

type City = {
  id: number;
  name: string;
  region?: Region;
};

type SelectOption = {
  label: string;
  value: number | string;
};

type UseAddressCityAndDistrictOptions = {
  address?: Address;
};

export function useAddressCityAndDistrict({
  address,
}: UseAddressCityAndDistrictOptions = {}) {
  const [loading, setLoading] = useState({
    regions: false,
    cities: false,
    zipCodes: false,
  });

  const [options, setOptions] = useState({
    regions: [] as SelectOption[],
    cities: [] as SelectOption[],
    zipCodes: [] as SelectOption[],
  });

  const [selectedRegion, setSelectedRegion] = useState<Region | null>(
    address?.region || null,
  );

  const [selectedCity, setSelectedCity] = useState<City | null>(
    address?.isOther
      ? { id: 0, name: trans("other") }
      : (address?.city as City) || null,
  );

  const [isOtherCitySelected, setIsOtherCitySelected] = useState(
    address?.isOther || false,
  );

  // Helpers to reduce repetitive setLoading / setOptions calls
  const startLoading = (key: keyof typeof loading) =>
    setLoading((prev) => ({ ...prev, [key]: true }));

  const stopLoading = (key: keyof typeof loading) =>
    setLoading((prev) => ({ ...prev, [key]: false }));

  const updateOptions = (key: keyof typeof options, value: SelectOption[]) =>
    setOptions((prev) => ({ ...prev, [key]: value }));

  // ── Data fetchers ──────────────────────────────────────────────

  const fetchRegions = useCallback(async () => {
    const regions = await getRegionsService(endpoint());

    return regions.map((region: Region) => ({
      label: getLocalizedValue(region.name),
      value: region.id,
    }));
  }, []);

  const fetchCities = useCallback(async () => {
    if (!selectedRegion) return [];

    const regionId =
      typeof selectedRegion === "object" ? selectedRegion.id : selectedRegion;

    const cities = await getCitiesService(endpoint(), { region: regionId });

    const cityOptions: SelectOption[] = cities.map((city: City) => ({
      label: getLocalizedValue(city.name),
      value: city.id,
    }));

    cityOptions.unshift({
      label: trans("other"),
      value: 0,
    });

    return cityOptions;
  }, [selectedRegion]);

  const fetchZipCodes = useCallback(async () => {
    if (!selectedCity) return [];

    const zipCodes = await getZipCodesService(endpoint(), {
      city: selectedCity.id,
    });

    return zipCodes.map((zipCode: { code: string; id: number }) => ({
      label: zipCode.code,
      value: zipCode.id,
    }));
  }, [selectedCity]);

  // ── Event handlers ─────────────────────────────────────────────

  const handleRegionChange = useCallback(
    (regionId: number) => {
      const region = options.regions.find((r) => r.value === regionId);

      setSelectedRegion(
        region ? { id: Number(region.value), name: region.label } : null,
      );
      setSelectedCity(null);
      setIsOtherCitySelected(false);
      setOptions((prev) => ({ ...prev, cities: [], zipCodes: [] }));
    },
    [options.regions],
  );

  const handleCityChange = useCallback(
    (cityId: number | string) => {
      const isOther = cityId === 0 || cityId === "other";

      setIsOtherCitySelected(isOther);
      updateOptions("zipCodes", []);

      if (isOther) {
        setSelectedCity({ id: 0, name: trans("other") });
        return;
      }

      const city = options.cities.find((c) => c.value === cityId);
      setSelectedCity(
        city ? { id: Number(city.value), name: city.label } : null,
      );
    },
    [options.cities],
  );

  // ── Effects ────────────────────────────────────────────────────

  // Load regions on mount
  useEffect(() => {
    startLoading("regions");

    fetchRegions()
      .then((regions) => updateOptions("regions", regions))
      .catch((error) => {
        console.error("Failed to load regions:", error);
        errorToast(parseError(error));
        updateOptions("regions", []);
      })
      .finally(() => stopLoading("regions"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cities when region changes
  useEffect(() => {
    if (!selectedRegion) {
      setOptions((prev) => ({ ...prev, cities: [], zipCodes: [] }));
      stopLoading("cities");
      return;
    }

    startLoading("cities");

    fetchCities()
      .then((cities) => updateOptions("cities", cities))
      .catch((error) => {
        console.error("Failed to load cities:", error);
        updateOptions("cities", []);
      })
      .finally(() => stopLoading("cities"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  // Load zip codes when city changes (skip if "Other" is selected)
  useEffect(() => {
    if (!selectedCity || isOtherCitySelected) {
      updateOptions("zipCodes", []);
      stopLoading("zipCodes");
      return;
    }

    startLoading("zipCodes");

    fetchZipCodes()
      .then((zipCodes) => updateOptions("zipCodes", zipCodes))
      .catch((error) => {
        console.error("Failed to load zip codes:", error);
        updateOptions("zipCodes", []);
      })
      .finally(() => stopLoading("zipCodes"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  return {
    loading,
    options,
    selectedRegion,
    selectedCity,
    isOtherCitySelected,
    handleRegionChange,
    handleCityChange,
  };
}
